import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import { io } from 'socket.io-client';
import { useAuth } from '../../contexts/AuthContext';
import { useRestaurants } from '../../hooks/useRestaurants';
import { api, API_URL } from '../../utils/apiClient';
import type { Product, Restaurant } from '../../types';
import ProductModal from './ProductModal';
import CreateRestaurantModal from './CreateRestaurantModal';
import NetworkCatalogPanel from './NetworkCatalogPanel';
import './MenuPanel.css';

function MenuPanel() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { restaurants, loading: loadingRestaurants, error: restaurantsError, fetchRestaurants } = useRestaurants();
  const [selectedRestaurantId, setSelectedRestaurantId] = useState('');
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [showCreateRestaurant, setShowCreateRestaurant] = useState(false);
  const [showCatManager, setShowCatManager] = useState(false);
  const [catalogCats, setCatalogCats] = useState<{ id: string; name: string }[]>([]);
  const [catalogEntries, setCatalogEntries] = useState<{ externalId: string; categoryId: string | null }[]>([]);
  useEffect(() => {
    if (!user?.networkId) return;
    api.get<{ categories: { id: string; name: string }[]; entries: { externalId: string; categoryId: string | null }[] }>('/api/menu-catalog')
      .then(({ data }) => { setCatalogCats(data.categories); setCatalogEntries(data.entries); }).catch(() => {});
  }, [user?.networkId]);
  useEffect(() => {
    fetchRestaurants().then((filtered) => {
      if (filtered.length > 0 && !selectedRestaurantId) setSelectedRestaurantId(filtered[0].id);
    });
  }, [fetchRestaurants]);

  const fetchProducts = useCallback(async () => {
    if (!selectedRestaurantId.trim()) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get<Product[]>(`/api/products/restaurant/${selectedRestaurantId}`);
      setProducts(data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [selectedRestaurantId]);

  useEffect(() => {
    if (selectedRestaurantId) {
      api.get<Restaurant>(`/api/restaurants/${selectedRestaurantId}`)
        .then(({ data }) => {
          setRestaurant(data);
          setError('');
        })
        .catch((err: any) => {
          if (err.response?.status === 404) setRestaurant(null);
          setError(err.response?.data?.error || 'Restaurant not found');
        });
      fetchProducts();
    } else {
      setProducts([]);
      setRestaurant(null);
    }
  }, [selectedRestaurantId, fetchProducts]);

  useEffect(() => {
    if (!selectedRestaurantId) return;
    const socketUrl = API_URL || (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? 'http://localhost:3001' : `${window.location.protocol}//${window.location.hostname}:3001`);
    const socket = io(socketUrl, { transports: ['websocket', 'polling'] });
    const onProductsUpdated = () => fetchProducts();
    socket.on('connect', () => socket.emit('join-restaurant', selectedRestaurantId));
    socket.emit('join-restaurant', selectedRestaurantId);
    socket.on('products-updated', onProductsUpdated);
    const poll = setInterval(onProductsUpdated, 2000);
    const onVisibilityChange = () => { if (document.visibilityState === 'visible') onProductsUpdated(); };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      socket.off('products-updated', onProductsUpdated);
      socket.close();
      clearInterval(poll);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [selectedRestaurantId, fetchProducts]);

  const handleCreate = () => {
    setEditingProduct(null);
    setShowModal(true);
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setShowModal(true);
  };

  const handleDelete = async (productId: string) => {
    if (!confirm(t('menu.confirmDelete'))) return;

    try {
      await api.patch(`/api/products/${productId}`, { isActive: false });
      fetchProducts();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete product');
    }
  };

  const handleToggleActive = async (product: Product) => {
    try {
      await api.patch(`/api/products/${product.id}`, { isActive: !product.isActive });
      fetchProducts();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update product');
    }
  };

  const handleToggleInstantGiveout = async (product: Product) => {
    const next = !product.instantGiveout;
    try {
      await api.patch(`/api/products/${product.id}`, { instantGiveout: next });
      setProducts(prev => prev.map(p => p.id === product.id ? { ...p, instantGiveout: next } : p));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update instant giveout');
    }
  };

  const handleUpdatePriority = async (product: Product, delta: number) => {
    const next = Math.max(0, (product.upsellPriority ?? 0) + delta);
    try {
      await api.patch(`/api/products/${product.id}`, { upsellPriority: next });
      setProducts(prev => prev.map(p => p.id === product.id ? { ...p, upsellPriority: next } : p));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update priority');
    }
  };

  const handleUpdateStock = async (product: Product, delta: number) => {
    const current = product.stockToday ?? null;
    let value: number | null;
    if (current === null) {
      value = delta > 0 ? 1 : 0; // from unlimited: + → 1, − → 0
    } else if (current === 0) {
      value = delta > 0 ? 1 : null; // from 0: + → 1, − → unlimited
    } else {
      const next = current + delta;
      value = next <= 0 ? (delta < 0 ? 0 : 1) : next;
    }
    try {
      await api.patch(`/api/products/${product.id}`, { stockToday: value });
      setProducts(prev => prev.map(p => p.id === product.id ? { ...p, stockToday: value } : p));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update stock');
    }
  };

  const categories = Array.from(new Set(products.map(p => p.category)));

  const filteredProducts = filterCategory === 'all'
    ? products
    : products.filter(p => p.category === filterCategory);

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    const aP = a.upsellPriority ?? 0;
    const bP = b.upsellPriority ?? 0;
    if (bP !== aP) return bP - aP;
    return a.name.localeCompare(b.name);
  });

  return (
    <div className="menu-panel">
      <div className="panel-card">
        <div className="panel-controls">
          <div className="control-group">
            <select
              value={selectedRestaurantId}
              onChange={(e) => setSelectedRestaurantId(e.target.value)}
              className="restaurant-select"
              disabled={loadingRestaurants}
            >
              <option value="">{t('menu.selectRestaurant')}</option>
              {restaurants.map(r => (
                <option key={r.id} value={r.id}>
                  {r.name} {r._count && `(${r._count.products} products)`}
                </option>
              ))}
            </select>
            <motion.button
              onClick={handleCreate}
              className="btn-secondary"
              disabled={!selectedRestaurantId}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
            >
              + {t('menu.addProduct')}
            </motion.button>
            {(user?.role === 'SUPER_ADMIN' || user?.role === 'DEVELOPER') && (
              <button onClick={() => setShowCreateRestaurant(true)} className="btn-create-restaurant">
                + {t('menu.createRestaurant')}
              </button>
            )}
          </div>
          {restaurant && (
            <div className="restaurant-info">
              <strong>{restaurant.name}</strong> • {products.length} products • {restaurant._count?.orders || 0} orders
              <label className="low-stock-threshold-label">
                {t('menu.lowStockThreshold')}:
                <input
                  type="number"
                  min="0"
                  value={restaurant.preferences?.lowStockThreshold ?? 5}
                  onChange={async (e) => {
                    const v = parseInt(e.target.value);
                    if (isNaN(v) || v < 0) return;
                    try {
                      await api.patch(`/api/restaurants/${selectedRestaurantId}/preferences`, { lowStockThreshold: v });
                      const { data } = await api.get<Restaurant>(`/api/restaurants/${selectedRestaurantId}`);
                      setRestaurant(data);
                    } catch (err) {
                      console.error(err);
                    }
                  }}
                  className="low-stock-threshold-input"
                />
              </label>
            </div>
          )}
        </div>

        {selectedRestaurantId && (
          <div className="menu-controls-row">
            <div className="filter-controls">
              <label>{t('menu.filterByCategory')}</label>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="category-filter"
              >
                <option value="all">{t('menu.allCategories')}</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              {user?.networkId && (
                <button
                  onClick={() => setShowCatManager(true)}
                  style={{ padding: '8px 14px', borderRadius: 10, border: '1px solid #e0dcd5', background: '#fff', cursor: 'pointer', fontSize: 13, color: '#1f1b16', whiteSpace: 'nowrap' }}
                  title="Порядок и названия категорий меню сети"
                >
                  ⇅ Категории
                </button>
              )}
            </div>
          </div>
        )}

        {(error || restaurantsError) && <div className="error-box">{error || restaurantsError}</div>}

        {!selectedRestaurantId ? (
          <div className="empty-state">
            {t('menu.selectRestaurantPrompt')}
          </div>
        ) : loading && products.length === 0 ? (
          <div className="loading-state">{t('menu.loadingProducts')}</div>
        ) : products.length === 0 ? (
          <div className="empty-state">
            {t('menu.noProductsFound')}
          </div>
        ) : (
          <div className="products-grid">
            {sortedProducts.map(product => (
              <div
                key={product.id}
                className={`product-card-admin ${!product.isActive ? 'inactive' : ''}`}
              >
                <div className="product-image-wrap">
                  {product.imageUrl ? (
                    <div
                      className="product-image"
                      style={{ backgroundImage: `url(${product.imageUrl})` }}
                    />
                  ) : (
                    <div className="product-image-placeholder">
                    </div>
                  )}
                  {product.stockToday != null && product.stockToday < (restaurant?.preferences?.lowStockThreshold ?? 5) && (
                    <span className="product-card-badge product-card-badge-low" title={t('menu.lowStockBadge')}>
                      {product.stockToday}
                    </span>
                  )}
                </div>

                <div className="product-content">
                  <div className="product-header">
                    <h3 className="product-name">{product.name}</h3>
                    <div className="product-badges">
                      {(product.upsellPriority ?? 0) > 0 && (
                        <span className="badge-upsell">{t('menu.inUpsell')}</span>
                      )}
                      {!product.isActive && (
                        <span className="badge-inactive">{t('menu.inactive')}</span>
                      )}
                      {product.stockToday != null && product.stockToday < (restaurant?.preferences?.lowStockThreshold ?? 5) && (
                        <span className="badge-low-stock" title={t('menu.lowStockBadge')}>
                          {product.stockToday} left
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="product-meta">
                    {user?.networkId && product.externalId ? (
                      <select
                        value={catalogEntries.find(e => e.externalId === product.externalId)?.categoryId || ''}
                        onChange={async (e) => {
                          await api.put(`/api/menu-catalog/entries/${encodeURIComponent(product.externalId!)}`, { categoryId: e.target.value || null });
                          setCatalogEntries(prev => {
                            const idx = prev.findIndex(en => en.externalId === product.externalId);
                            const updated = { externalId: product.externalId!, categoryId: e.target.value || null };
                            return idx >= 0 ? prev.map((en, i) => i === idx ? updated : en) : [...prev, updated];
                          });
                        }}
                        className="category-filter"
                      >
                        <option value="">— без категории —</option>
                        {catalogCats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    ) : (
                      <span className="product-category">{product.category}</span>
                    )}
                    <span className="product-price">₸{product.price.toFixed(2)}</span>
                  </div>

                  {product.description && (
                    <p className="product-description">{product.description}</p>
                  )}

                  <div className="product-steppers">
                    <div className="product-stepper">
                      <label>{t('menu.priority')}</label>
                      <div className="product-stepper-btns">
                        <button type="button" onClick={(e) => { e.stopPropagation(); handleUpdatePriority(product, -1); }}>−</button>
                        <span>{product.upsellPriority ?? 0}</span>
                        <button type="button" onClick={(e) => { e.stopPropagation(); handleUpdatePriority(product, 1); }}>+</button>
                      </div>
                    </div>
                    <div className="product-stepper">
                      <label>{t('menu.stockToday')}</label>
                      <div className="product-stepper-btns">
                        <button type="button" onClick={(e) => { e.stopPropagation(); handleUpdateStock(product, -1); }}>−</button>
                        <span>{product.stockToday ?? '∞'}</span>
                        <button type="button" onClick={(e) => { e.stopPropagation(); handleUpdateStock(product, 1); }}>+</button>
                      </div>
                    </div>
                    <button
                      type="button"
                      className={`product-instant-giveout-toggle${product.instantGiveout ? ' active' : ''}`}
                      onClick={(e) => { e.stopPropagation(); handleToggleInstantGiveout(product); }}
                      title={t('menu.instantGiveoutHint')}
                    >
                      {t('menu.instantGiveout')}
                    </button>
                  </div>

                  {(product.tags?.length ?? 0) > 0 && (
                    <div className="product-tags">
                      {(product.tags ?? []).map(tag => (
                        <span key={tag} className="tag">{tag}</span>
                      ))}
                    </div>
                  )}

                  <div className="product-actions">
                    <button
                      onClick={() => handleEdit(product)}
                      className="btn-edit"
                    >
                      {t('menu.edit')}
                    </button>
                    <button
                      onClick={() => handleToggleActive(product)}
                      className={product.isActive ? 'btn-deactivate' : 'btn-activate'}
                    >
                      {product.isActive ? t('menu.deactivate') : t('menu.activate')}
                    </button>
                    <button
                      onClick={() => handleDelete(product.id)}
                      className="btn-delete"
                    >
                      {t('menu.delete')}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showCatManager && user?.networkId && (
        <NetworkCatalogPanel
          onClose={() => { setShowCatManager(false); fetchProducts(); }}
          onChanged={() => {
            api.get<{ categories: { id: string; name: string }[]; entries: { externalId: string; categoryId: string | null }[] }>('/api/menu-catalog')
              .then(({ data }) => { setCatalogCats(data.categories); setCatalogEntries(data.entries); }).catch(() => {});
          }}
        />
      )}

      {showModal && (
        <ProductModal
          restaurantId={selectedRestaurantId}
          product={editingProduct}
          onClose={() => {
            setShowModal(false);
            setEditingProduct(null);
          }}
          onSuccess={() => {
            setShowModal(false);
            setEditingProduct(null);
            fetchProducts();
          }}
        />
      )}

      {showCreateRestaurant && (
        <CreateRestaurantModal
          onClose={() => setShowCreateRestaurant(false)}
          onSuccess={(restaurantId) => {
            setSelectedRestaurantId(restaurantId);
            setShowCreateRestaurant(false);
            fetchRestaurants();
          }}
        />
      )}
    </div>
  );
}

export default MenuPanel;

